// src/components/Dashboard/LoanReadinessCard.tsx
// 대출 준비도 카드 — 2026 소상공인 특화 신용평가체계(SCB) 대비 참고 지표.
//
// 금융위 2026-04-09 「SCB 도입방안」: 은행 소상공인 대출 심사가 대표자
// 개인신용 외에 매출 흐름·상권·성장세를 반영 (2026-08-31 8개 은행 시범운영).
// 가계부 데이터로 산출 가능한 3축(매출 성장세·기록 꾸준함·카드매출 비중)만
// 표시하고, 점수·등급은 산출하지 않는다 — 실제 CB·은행 평가로 오인 방지.
// 상권 비교 지표는 앱(동네 탭)에서 제공 (PC 는 매장 주소 데이터 없음).
import React, { useMemo } from 'react';
import { Box, Chip, Paper, Stack, Tooltip, Typography } from '@mui/material';
import SectionHeader from '../SectionHeader';
import { useAccountBookStore } from '@/store/accountBookStore';

type Status = 'good' | 'normal' | 'attention' | 'unknown';

const STATUS_LABEL: Record<Status, string> = {
  good: '좋아요',
  normal: '보통',
  attention: '관리 필요',
  unknown: '정보 없음',
};

const STATUS_COLOR: Record<Status, { fg: string; bg: string }> = {
  good: { fg: 'var(--c-green-700)', bg: 'var(--primary-soft)' },
  normal: { fg: 'var(--fg-secondary)', bg: 'var(--bg-muted)' },
  attention: { fg: 'var(--c-red-600)', bg: 'var(--bg-muted)' },
  unknown: { fg: 'var(--fg-tertiary)', bg: 'var(--bg-muted)' },
};

/** 카드·PG 매출 계열 — CB의 매출 추정 원천 (앱 IncomeCategory와 동일 기준) */
const CARD_SALES_CATEGORIES = new Set(['카드', '배달앱', '플랫폼']);

interface Indicator {
  label: string;
  detail: string;
  value: string;
  status: Status;
  tip: string;
}

const LoanReadinessCard: React.FC = () => {
  const dashboardData = useAccountBookStore((s) => s.dashboardData);

  const indicators = useMemo<Indicator[]>(() => {
    const now = new Date();
    const isCurrentYear = dashboardData.selectedYear === now.getFullYear();
    // 완결된 마지막 월 인덱스 (이번 달은 진행 중이라 제외; 과거 연도는 12월까지)
    const lastClosedMonth = isCurrentYear ? now.getMonth() : 12; // 1-based 개수
    const monthly = dashboardData.monthlyData
      .filter((m) => m.month <= lastClosedMonth)
      .sort((a, b) => a.month - b.month);

    // ── 매출 성장세: 최근 3개월 vs 직전 3개월 ──
    let growth: number | null = null;
    if (monthly.length >= 6) {
      const recent3 = monthly.slice(-3).reduce((s, m) => s + m.income, 0);
      const prev3 = monthly.slice(-6, -3).reduce((s, m) => s + m.income, 0);
      growth = prev3 > 0 ? (recent3 / prev3 - 1) * 100 : null;
    }
    const growthStatus: Status =
      growth == null ? 'unknown' : growth >= 5 ? 'good' : growth >= -5 ? 'normal' : 'attention';

    // ── 기록 꾸준함: 최근 6개(완결) 월 중 매출 기록 월 수 ──
    const window6 = monthly.slice(-6);
    const recorded = window6.filter((m) => m.income > 0).length;
    const consistencyStatus: Status =
      window6.length === 0
        ? 'unknown'
        : recorded >= 6
          ? 'good'
          : recorded >= 4
            ? 'normal'
            : 'attention';

    // ── 카드매출 비중: 매출 소스 중 카드·배달앱·플랫폼 비중 ──
    const totalRevenue = dashboardData.revenueSources.reduce((s, r) => s + r.amount, 0);
    const cardRevenue = dashboardData.revenueSources
      .filter((r) => CARD_SALES_CATEGORIES.has(r.category))
      .reduce((s, r) => s + r.amount, 0);
    const cardRatio = totalRevenue > 0 ? cardRevenue / totalRevenue : null;
    const cardStatus: Status =
      cardRatio == null ? 'unknown' : cardRatio >= 0.7 ? 'good' : cardRatio >= 0.4 ? 'normal' : 'attention';

    return [
      {
        label: '매출 성장세',
        detail: '최근 3개월 vs 직전 3개월 (진행 중인 달 제외)',
        value: growth == null ? '비교할 기록 부족' : `${growth >= 0 ? '+' : ''}${growth.toFixed(1)}%`,
        status: growthStatus,
        tip: '최근 매출이 줄고 있어요. 새 평가체계(SCB)는 성장 흐름을 봐요 — 매출 회복 흐름을 만들어보세요.',
      },
      {
        label: '기록 꾸준함',
        detail: '최근 6개월 중 매출 기록이 있는 달',
        value: window6.length === 0 ? '기록 없음' : `${recorded}개월 / ${window6.length}개월`,
        status: consistencyStatus,
        tip: '기록 공백이 있으면 매출 흐름을 증빙하기 어려워요. 꾸준한 기록이 곧 대출 준비예요.',
      },
      {
        label: '카드매출 비중',
        detail: '카드·배달앱·플랫폼 매출 비율',
        value: cardRatio == null ? '매출 기록 없음' : `${Math.round(cardRatio * 100)}%`,
        status: cardStatus,
        tip: '카드·플랫폼 매출은 신용평가사(CB)가 매출을 추정하는 근거예요. 비중이 높을수록 증빙에 유리해요.',
      },
    ];
  }, [dashboardData]);

  const tip = useMemo(() => {
    const worst =
      indicators.find((i) => i.status === 'attention') ??
      indicators.find((i) => i.status === 'normal');
    return worst?.tip ?? null;
  }, [indicators]);

  // 데이터가 전혀 없으면 숨김 (텅 빈 계정에 경고성 카드 노출 방지)
  if (indicators.every((i) => i.status === 'unknown')) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        background: 'var(--bg-base)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--r-20)',
        p: { xs: 2, sm: 2.5, md: 3 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <SectionHeader size="card" eyebrow="2026 신용평가 개편" title="성장 신용도" />
        <Chip
          size="small"
          label="참고용"
          sx={{ backgroundColor: 'var(--bg-muted)', color: 'var(--fg-secondary)', fontWeight: 600 }}
        />
      </Box>
      <Typography sx={{ mt: 1, fontSize: 'var(--size-sm)', color: 'var(--fg-secondary)', lineHeight: 1.6 }}>
        은행 대출 심사가 바뀌었어요. 동네로 오는 인구 흐름(상권)과 내 매장의 매출 성장을 합쳐서 보는
        새 평가(SCB)가 2026년 8월부터 주요 은행에서 시범운영 중입니다.
      </Typography>

      <Stack spacing={1.25} sx={{ mt: 2 }}>
        {indicators.map((ind) => (
          <Box
            key={ind.label}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minHeight: 40 }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 'var(--size-sm)', fontWeight: 600, color: 'var(--fg-default)' }}>
                {ind.label}
              </Typography>
              <Typography sx={{ fontSize: 'var(--size-xs)', color: 'var(--fg-tertiary)' }} noWrap>
                {ind.detail}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontVariantNumeric: 'tabular-nums',
                fontWeight: 700,
                fontSize: 'var(--size-sm)',
                color: 'var(--fg-default)',
                whiteSpace: 'nowrap',
              }}
            >
              {ind.value}
            </Typography>
            <Tooltip title={ind.tip} arrow>
              <Chip
                size="small"
                label={STATUS_LABEL[ind.status]}
                sx={{
                  backgroundColor: STATUS_COLOR[ind.status].bg,
                  color: STATUS_COLOR[ind.status].fg,
                  fontWeight: 700,
                  minWidth: 64,
                }}
              />
            </Tooltip>
          </Box>
        ))}
      </Stack>

      {tip && (
        <Box
          sx={{
            mt: 2,
            px: 1.5,
            py: 1.25,
            borderRadius: 'var(--r-12, 12px)',
            backgroundColor: 'var(--primary-soft)',
          }}
        >
          <Typography sx={{ fontSize: 'var(--size-sm)', color: 'var(--primary-press)', lineHeight: 1.6 }}>
            💡 {tip}
          </Typography>
        </Box>
      )}

      <Typography sx={{ mt: 1.5, fontSize: 'var(--size-xs)', color: 'var(--fg-tertiary)', lineHeight: 1.5 }}>
        가계부 기록 기반 참고 지표입니다. 실제 은행·신용평가사(CB) 심사 등급이 아니며 대출 결과와 다를 수
        있어요. 상권 대비 매출 위치는 앱의 동네 탭에서 확인할 수 있어요.
      </Typography>
    </Paper>
  );
};

export default LoanReadinessCard;
